import { useEffect, useState } from 'react';
import { Card } from '../components/Card';
import { HealthKit } from '../native/healthKit';
import { normalizeHealthKitSamples } from '@vitalai/health-normalizer';
import type { NormalizedDailyMetrics } from '@vitalai/health-models';
import { formatTime } from '../services/labels';

type Availability = 'checking' | 'available' | 'unavailable';
type AuthState = 'idle' | 'requesting' | 'granted' | 'error';
type SyncState = 'idle' | 'syncing' | 'error';

const PERMISSION_CATEGORIES = ['Sleep', 'Heart Rate', 'HRV', 'SpO2', 'Activity', 'Workouts'];

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function HealthDebugScreen() {
  const [availability, setAvailability] = useState<Availability>('checking');
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [authError, setAuthError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('idle');
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [rawSampleCount, setRawSampleCount] = useState<number>(0);
  const [normalizedDays, setNormalizedDays] = useState<NormalizedDailyMetrics[]>([]);

  useEffect(() => {
    HealthKit.isAvailable()
      .then((result) => setAvailability(result.available ? 'available' : 'unavailable'))
      .catch(() => setAvailability('unavailable'));
  }, []);

  const handleConnect = async () => {
    setAuthState('requesting');
    setAuthError(null);
    try {
      const result = await HealthKit.requestAuthorization();
      setAuthState(result.granted ? 'granted' : 'error');
      if (!result.granted) setAuthError('HealthKit no otorgo permisos de lectura.');
    } catch (error) {
      setAuthState('error');
      setAuthError(errorMessage(error));
    }
  };

  const handleSync = async (days: number) => {
    setSyncState('syncing');
    setSyncError(null);
    try {
      const endDate = Date.now();
      const startDate = endDate - days * 24 * 60 * 60 * 1000;
      const result = await HealthKit.queryDailyMetrics({ startDate, endDate });
      setRawSampleCount(result.samples.length);
      setNormalizedDays(normalizeHealthKitSamples(result.samples));
      setLastSyncAt(new Date().toISOString());
      setSyncState('idle');
    } catch (error) {
      setSyncState('error');
      setSyncError(errorMessage(error));
    }
  };

  return (
    <div className="screen screen--health-debug">
      <p className="banner-note">
        Pantalla temporal de diagnostico para el Sprint 2. No forma parte de la navegacion principal y se retira antes
        de produccion.
      </p>

      <Card title="Disponibilidad">
        <p className="debug-row">
          HealthKit disponible:{' '}
          <strong>{availability === 'checking' ? 'Verificando…' : availability === 'available' ? 'Si' : 'No'}</strong>
        </p>
        {availability === 'unavailable' ? (
          <p className="hint-text">
            Esperado si estas viendo esta pantalla en un navegador (npm run dev). Solo es "Si" dentro de la app nativa
            de iOS, tras compilarla con Xcode.
          </p>
        ) : null}
      </Card>

      <Card title="Permisos" subtitle="Categorias solicitadas a HealthKit">
        <ul className="source-list">
          {PERMISSION_CATEGORIES.map((category) => (
            <li key={category} className="source-list__item">
              <span>{category}</span>
              <span className="source-list__status">
                {authState === 'granted' ? 'Solicitado' : authState === 'requesting' ? 'Solicitando…' : 'Pendiente'}
              </span>
            </li>
          ))}
        </ul>
        <p className="hint-text">
          iOS no expone, por privacidad, si el usuario otorgo o nego cada permiso de lectura individualmente: solo
          sabemos que la solicitud de autorizacion se completo.
        </p>
        <button type="button" onClick={handleConnect} disabled={authState === 'requesting'}>
          Connect Apple Health
        </button>
        {authError ? <p className="debug-error">{authError}</p> : null}
      </Card>

      <Card title="Sincronizacion">
        <p className="debug-row">
          Ultima sincronizacion: <strong>{lastSyncAt ? formatTime(lastSyncAt) : 'Nunca'}</strong>
        </p>
        <div className="debug-button-row">
          <button type="button" onClick={() => handleSync(1)} disabled={syncState === 'syncing'}>
            Sync Today
          </button>
          <button type="button" onClick={() => handleSync(7)} disabled={syncState === 'syncing'}>
            Sync 7 Days
          </button>
          <button type="button" onClick={() => handleSync(30)} disabled={syncState === 'syncing'}>
            Sync 30 Days
          </button>
        </div>
        {syncState === 'syncing' ? <p className="hint-text">Sincronizando…</p> : null}
        {syncError ? <p className="debug-error">{syncError}</p> : null}
        {lastSyncAt ? <p className="hint-text">{rawSampleCount} muestras crudas recibidas de HealthKit.</p> : null}
      </Card>

      {normalizedDays.length > 0 ? (
        <Card title="Datos normalizados" subtitle={`${normalizedDays.length} dia(s)`}>
          <ul className="workout-list">
            {normalizedDays.map((day) => (
              <li key={day.date} className="workout-list__item workout-list__item--start">
                <div>
                  <p className="workout-list__type">{day.date}</p>
                  <p className="workout-list__time">source: {day.source}</p>
                  <p className="workout-list__time">
                    sleep: {formatTime(day.sleep.sleepStart)} - {formatTime(day.sleep.wakeTime)} (
                    {day.sleep.totalMinutes} min)
                  </p>
                  <p className="workout-list__time">
                    HRV {day.recovery.hrv} ms · FC reposo {day.recovery.restingHeartRate} lpm · SpO2 {day.recovery.spo2}%
                  </p>
                  <p className="workout-list__time">
                    pasos {day.activity.steps} · calorias {day.activity.activeCalories} · ejercicio{' '}
                    {day.activity.exerciseMinutes} min · {day.activity.workouts.length} entrenamiento(s)
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
